import { createRouteMethodologyFilter, defineDashboard, resolveProduct } from '../../../_shared/dashboard-axes';
import { createTimeFilters, resolveTimeFilter } from '../../../_shared/time-filters';
import { CreateDashboardDefinitionBase } from '../../../_shared/dashboard-definition.model';
import { createIdNameTransformers } from '../../../../dashboard-config/granularities/granularity-profile';
import {
  AggregatedRowChildrenAttacherDashboardDataTransfomer,
  GeoSumAppenderDashboardDataTransformer,
  MarketSumAppenderDashboardDataTransformer,
  UndefinedStateToSumGermanyDashboardDataTransformer,
} from '../../../../data_transformers/dashboard_data_transformer';
import {
  DashboardDistrictsMultiSelectFilter,
  DashboardRemoteStateSingleSelectFilter,
  DashboardSubMarketsMultiSelectFilter,
} from '../../../../filters/dashboard_filter_implementations';
import { createKpiFilters } from '../../../../dashboard-config/kpis/kpi-filters';
import { KpiExportDataTransformer } from '../../../../dashboard-config/kpis/kpi-data-transformers';
import {
  ComparisonStructureMetricProfile,
} from './dashboard-comparison-structure-metric.profile';
import { DashboardComparisonStructureDefinition } from './dashboard-comparison-structure.definition';
import {
  DashboardComparisonStructureShell,
  dashboardComparisonStructureShellProfiles,
} from './dashboard-comparison-structure-shell.profile';

/** Product-owned UI / data keys for the structure shell. */
export interface DashboardComparisonStructureViewInput {
  /** STRUCTURE heading fragment, e.g. UNTERNEHMENSTRUKTUR */
  structureLabel: string;
  /** Raw dataset column used by structure charts (not filter→value mapping). */
  valueKey: string;
  chartMetricTitle: string;
  popupValueLabel: string;
}

export type CreateDashboardComparisonStructureDefinitionInput =
  CreateDashboardDefinitionBase<
    DashboardComparisonStructureShell,
    ComparisonStructureMetricProfile
  >;

export function createDashboardComparisonStructureDefinition(
  input: CreateDashboardComparisonStructureDefinitionInput,
): DashboardComparisonStructureDefinition {
  const { axes, shell, metric } = resolveProduct(input, dashboardComparisonStructureShellProfiles);
  const { structureLabel, valueKey, chartMetricTitle, popupValueLabel } = metric;
  const time = resolveTimeFilter(axes, 'single');

  const granularityId = axes.granularity.id;
  const metricFilters = createKpiFilters(axes.metric, granularityId);

  // Both sides of the view build their own filters, each with its default state.
  const createFilters: DashboardComparisonStructureDefinition['createFilters'] = (deps, options) => [
    ...createTimeFilters(deps, time),
    new DashboardRemoteStateSingleSelectFilter(deps.localDatasetsService, {
      addSumGermany: true,
      defaultValue: options.stateDefault,
      ignoreProductSetting: true,
    }),
    new DashboardDistrictsMultiSelectFilter(deps.localDatasetsService, { parentFilterName: 'state' }),
    new DashboardSubMarketsMultiSelectFilter(deps.localDatasetsService, { excludedValues: deps.sumSubMarketIds }),
    ...metricFilters,
    createRouteMethodologyFilter(deps),
  ];

  return defineDashboard<DashboardComparisonStructureDefinition>(axes, shell, {
    heading: shell.createHeading(structureLabel),
    createFilters,
    filters: (deps) =>
      createFilters(deps, { stateDefault: shell.sideDefaults.stateA }),
    preFilterTransformers: () => [new UndefinedStateToSumGermanyDashboardDataTransformer()],
    postFilterTransformers: (deps) => [
      ...createIdNameTransformers(deps, granularityId),
      new GeoSumAppenderDashboardDataTransformer(valueKey, 'district_id', 'sub_market'),
      new MarketSumAppenderDashboardDataTransformer(valueKey, 'sub_market_id', 'district'),
    ],
    chartTransformers: () => [
      new AggregatedRowChildrenAttacherDashboardDataTransfomer({
        aggregateIdColumn: 'sub_market_id',
        childrenIdColumn: 'district_id',
        resultColumn: 'geoData',
      }),
      new AggregatedRowChildrenAttacherDashboardDataTransfomer({
        aggregateIdColumn: 'district_id',
        childrenIdColumn: 'sub_market_id',
        resultColumn: 'marketData',
      }),
    ],
    exportTransformers: () => [new KpiExportDataTransformer({ kpis: axes.metric.kpis(), percentageMeasures: [] })],
    view: {
      valueKey,
      chartMetricTitle,
      popupValueLabel,
      structureLabel,
      geo: shell.geo,
      market: shell.market,
      sideDefaults: shell.sideDefaults,
    },
  });
}
